import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Drawer, Typography, Radio } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { TrimbleColors } from 'legacy-components/TrimbleColors';
import { FormattedMessage } from 'react-intl';
import UnifiedAPIService, { UnifiedAPI } from 'services/UnifiedAPIService';
import { sanitizeText } from '../product-forms/FileXSSRemover';
import { useAsyncAction } from 'legacy-components/form/action/AsyncAction';
import { from } from 'rxjs';
import { InputContainer } from 'modus/components/form/input-container/InputContainer';
import VisibilitySelector, { VisibilityType } from './VisibilitySelector';
import ApprovalFlowSelector, { ApprovalType } from './ApprovalFlowSelector';

const useStyles = makeStyles(theme => ({
  drawer: {
    width: '768px',
    position: 'relative',
    marginLeft: 'auto',
    boxSizing: 'border-box',
    padding: '37px 28px',
    boxShadow: '-3px 4px 2px 1px rgba(0, 0, 0, 0.25)',
    background: '#FFFFFF',
    top: '60px',
    right: 0,
    height: '100%',
  },
  modalRoot: { position: 'static', top: '50px' },
  backdrop: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 0,
  },
  formContainer: {
    padding: '10px 20px 0px 0px',
  },
  title: {
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0.15px',
    color: '#002D5B',
    marginBottom: '25px',
  },
  formGroup: { marginBottom: '30px' },
  formLabel: {
    fontWeight: 700,
    fontSize: '12px',
    lineHeight: '18px',
    letterSpacing: '0.15px',
    color: '#464B52',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '2px 0px',
    gap: '4px',
  },
  radioControl: {
    '& .MuiRadio-root': { padding: '4px' },
    '& .MuiSvgIcon-root': { borderRadius: '50%' },
    '& .Mui-checked .MuiSvgIcon-root': { color: '#217CBB' }
  },
  buttonBase: {
    textTransform: 'none',
    padding: '4px 8px',
    fontSize: '14px',
    minWidth: '81px',
    fontFamily: "'Open Sans'",
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '0.0015em',
    borderRadius: '4px',
  },
  submitButton: {
    backgroundColor: TrimbleColors.Blue,
    color: '#FFFFFF',
    height: '32px',
    '&:disabled': { opacity: 0.3 },
    extend: 'buttonBase',
  },
  cancelButton: {
    marginRight: '16px',
    border: '1px solid #252A2E',
    color: '#252A2E',
    extend: 'buttonBase',
  },
  badge: {
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 700,
    color: '#252A2E',
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    marginLeft: '12px',
    fontFamily: "'Open Sans'",
    lineHeight: '16px',
    height: '20px',
  },
  formLabelTypography: {
    height: '18px', 
    fontWeight: 700, 
    fontSize: '12px', 
    lineHeight: '18px', 
    letterSpacing: '0.15px',
  },
  requiredStar: {
    color: '#DA212C',
    marginLeft: '2px',
  },
  radioOptionBox: {
    width: '100%',
  },
  radioOptionDescription: {
    fontSize: '13px',
    color: '#555',
    marginTop: '2px',
    lineHeight: 1.4,
  },
  productNameInput: {
    fontSize: '12px',
    height: '16px',
    padding: '10px 14px',
  },
  formGroupWithMargin: {
    marginTop: '20px',
  },
  descriptionInput: {
    fontSize: '12px',
    fontWeight: 400,
    height: '36px',
    padding: '10px 14px',
  },
  descriptionStyle: {
    borderRadius: '2px',
    width: '100%',
  },
  buttonsContainer: {
    position: 'relative',
  },
}));

type ProductFormData = {
  name: string;
  description: string;
  visibility: string;
  approvalFlow: string;
  id?: string;
}

type AddProductFormProps = {
  open: boolean;
  onClose: () => void;
  onSubmit?: (product: ProductFormData, shouldNavigate?: boolean) => void;
  product?: any;
  isEditing?: boolean;
}

type RadioOptionProps = {
  checked: boolean;
  onChange: () => void;
  value: string;
  label: React.ReactNode;
  description: React.ReactNode;
  badge: React.ReactNode;
  radioClass: string;
}

export const FormLabel = ({ required, children }: { required?: boolean; children: React.ReactNode }) => {
  const classes = useStyles();
  return (
    <Typography className={classes.formLabelTypography}>
      {children}
      {required && <span className={classes.requiredStar}>*</span>}
    </Typography>
  );
};

export const RadioOption = ({ 
  checked, 
  onChange, 
  value, 
  label, 
  description, 
  badge, 
  radioClass 
}: RadioOptionProps) => {
  const classes = useStyles();
  return (
    <Box className={classes.radioOptionBox}>
      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
        <Box display="flex">
          <Radio
            checked={checked}
            onChange={onChange}
            value={value}
            color="primary"
            className={radioClass}
          />
          <Box display="flex" flexDirection="column" alignItems="flex-start">
            <Typography>{label}</Typography>
            <Typography className={classes.radioOptionDescription}>
              {description}
            </Typography>
          </Box>
        </Box>
        {badge}
      </Box>
    </Box>
  );
};

export const AddProductForm: React.FC<AddProductFormProps> = ({ open, onClose, onSubmit, product, isEditing = false }) => {
  const classes = useStyles();

  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [nameError, setNameError] = useState(false);
  const [descError, setDescError] = useState(false);
  const [visibility, setVisibility] = useState<VisibilityType>('team');
  const [approvalFlow, setApprovalFlow] = useState<ApprovalType>('open');
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Since visibility values are the same between UI and API, we can use a single function
  const getVisibilityValue = useCallback((value: string): string => {
    const mapping: Record<string, string> = {
      'team': 'team',
      'account': 'account',
      'organization': 'organization',
      'public': 'public'
    };
    return mapping[value] || (value === '' ? 'team' : value);
  }, []);

  const getApprovalFlowUiValue = useCallback((apiValue: string): string => {
    const mapping: Record<string, string> = {
      'MANUAL': 'closed',
      'AUTO': 'open'
    };
    return mapping[apiValue] || 'open';
  }, []);

  const [createProductState, { subscribe: createProduct }] = useAsyncAction(() => 
    from(UnifiedAPIService.post(
      UnifiedAPI.product_api,
      {
        name: name.trim(),
        displayName: sanitizeText(name.trim()),
        longDescription: sanitizeText(description.trim()),
        visibility: getVisibilityValue(visibility),
        approvalType: approvalFlow === 'open' ? 'AUTO' : 'MANUAL'
      }
    ))
  );
  
  const [editProductState, { subscribe: editProduct }] = useAsyncAction(() => 
    from(UnifiedAPIService.put(
      UnifiedAPI.parseRequestURL(UnifiedAPI.edit_product_api, {
        id: product?.name
      }),
      {
        displayName: sanitizeText(name.trim()),
        longDescription: sanitizeText(description.trim()),
        visibility: getVisibilityValue(visibility),
        approvalType: approvalFlow === 'open' ? 'AUTO' : 'MANUAL'
      }
    ))
  );

  useEffect(() => {
    if (isEditing && product) {
      setName(product.displayName || product.name || '');
      setDescription(product.longDescription || '');
      setVisibility(getVisibilityValue(product.visibility || 'team') as VisibilityType);
      
      if (product.publishedInfo && product.publishedInfo.length > 0) {
        const approvalType = product.publishedInfo[0]?.approvalType;
        if (approvalType) {
          setApprovalFlow(getApprovalFlowUiValue(approvalType) as ApprovalType);
        }
      }
    }
  }, [isEditing, product, getVisibilityValue, getApprovalFlowUiValue]);

  useEffect(() => {
    const isValid = name.trim() !== '' && !nameError && !descError;
    setIsFormValid(isValid);
  }, [name, description, nameError, descError]);
  
  useEffect(() => {
    if (createProductState.error) {
      const error = createProductState.error as any;
      const status = error?.status || error?.response?.status;
      
      if (status !== 403 && status !== 500) {
        if (
          createProductState?.errorValue &&
          typeof createProductState.errorValue === 'object' &&
          'response' in createProductState.errorValue &&
          (createProductState.errorValue as any).response?.data?.message
        ) {
          setError((createProductState.errorValue as any).response.data.message);
        } else {
          setError('Error creating product. Please try again.');
        }
      } else {
        setError(null);
      }
    }
    
    if (editProductState.error) {
      const error = editProductState.error as any;
      const status = error?.status || error?.response?.status;
      
      if (status !== 403 && status !== 500) {
        if (
          editProductState?.errorValue &&
          typeof editProductState.errorValue === 'object' &&
          'response' in editProductState.errorValue &&
          (editProductState.errorValue as any).response?.data?.message
        ) {
          setError((editProductState.errorValue as any).response.data.message);
        } else {
          setError('Error updating product. Please try again.');
        }
      } else {
        setError(null);
      }
    }
    
    const createProductData = (id?: string) => ({
      name: name.trim(),
      description: description.trim(),
      visibility: visibility,
      approvalFlow: approvalFlow,
      ...(id && { id })
    });
    
    if (createProductState.value && !createProductState.loading && createProductState.loaded) {
      const id = createProductState.value?.data?.id || name.trim();
      
      if (onSubmit) {
        onSubmit(createProductData(id), true);
      }
      
      setName('');
      setDescription('');
      setVisibility('team');
      setApprovalFlow('open');
      onClose();
    }
    
    if (editProductState.value && !editProductState.loading && editProductState.loaded) {
      if (onSubmit) {
        onSubmit(createProductData(), false);
      }
      
      onClose();
    }
  }, [createProductState, editProductState, name, description, visibility, approvalFlow, onSubmit, onClose]);
  
  const handleSubmit = useCallback((event: React.FormEvent) => {
    event.preventDefault();
    if (!isFormValid) return;
    
    setError(null);
    (isEditing && product ? editProduct : createProduct)(false);
  }, [isFormValid, isEditing, product, createProduct, editProduct]);

  return (
    <Drawer
      anchor="right"
      open={open}
      variant="temporary"
      onClose={onClose}
      classes={{
        paper: classes.drawer,
      }}
      ModalProps={{
        classes: {
          root: classes.modalRoot,
          backdrop: classes.backdrop
        },
        hideBackdrop: false,
      }}
      SlideProps={{
        mountOnEnter: true,
        unmountOnExit: true,
      }}
    >
      <Box className={classes.formContainer}>
        <Typography variant="h3" className={classes.title}>
          {isEditing ? 
            <FormattedMessage defaultMessage="Edit Product" /> : 
            <FormattedMessage defaultMessage="Add new Product" />
          }
        </Typography>
        <form onSubmit={handleSubmit}>
          {error && (
            <Box mt={2}>
              <Typography color="error" variant="body2">{error}</Typography>
            </Box>
          )}
          <Box className={classes.formGroup}>
            <InputContainer
              label={<FormLabel><FormattedMessage defaultMessage="Name" /></FormLabel>}
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              setError={setNameError}
              validate={{
                required: true,
                maxLength: 255,
                allowSpace: true,
                pattern: '^[A-Za-z0-9 _-]+$',
                patternMessage: "Invalid Product Name. Only alpha-numerals, spaces, hyphen and underscore are allowed."
              }}
              placeholder="Enter product name"
              name="product-name"
              inputStyle={{ fontSize: '12px', height: '16px', padding: '10px 14px' }}
              autoFocus={!isEditing}
              disabled={isEditing && product}
              helperText={isEditing && product ? "Product name cannot be changed" : ""}
            />
          </Box>
          <Box className={`${classes.formGroup} ${classes.formGroupWithMargin}`}>
            <InputContainer
              label={<FormLabel><FormattedMessage defaultMessage="Description" /></FormLabel>}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              setError={setDescError}
              validate={{ required: false, maxLength: 140, allowSpace: true }}
              placeholder="Enter product description"
              name="product-description"
              inputStyle={{ fontSize: '12px', fontWeight: 400, height: '36px', padding: '10px 14px' }}
              multiline
              rows={3}
              className={classes.descriptionStyle}
            />
          </Box>
          <VisibilitySelector 
            visibility={visibility}
            onVisibilityChange={(newVisibility) => setVisibility(newVisibility)}
          />
          <ApprovalFlowSelector 
            approvalFlow={approvalFlow}
            onApprovalFlowChange={(newApprovalFlow) => setApprovalFlow(newApprovalFlow)}
          />
          
          <Box display="flex" justifyContent="flex-end" mt={5} mb={3} className={classes.buttonsContainer}>
            <Button
              onClick={onClose}
              variant="outlined"
              className={classes.cancelButton}
              disabled={createProductState.loading || editProductState.loading}
            >
              <FormattedMessage defaultMessage="Cancel" />
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!isFormValid || createProductState.loading || editProductState.loading}
              className={classes.submitButton}
            >
              {isEditing ? 
                <FormattedMessage defaultMessage="Update" /> : 
                <FormattedMessage defaultMessage="Create" />
              }
            </Button>
          </Box>

        </form>
      </Box>
    </Drawer>
  );
};

export default AddProductForm;
